// 목록 화면 상태/중요도/타입 등 단일선택 필터 칩 -> ToggleButtonGroup.
// /chancemng 프로토타입(2026-10-22, fc30533)의 ChanceStatusList.js FilterGroup을 추출.
import { useCallback } from 'react';
import { Box, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';

// 색 점: 칩 대신 토글버튼 안에 작게 두어 눈에 덜 띄게 한다.
// 다른 화면의 비배타(다중선택) ToggleButtonGroup에서도 같은 점을 쓰려고 내보낸다
// (예: partnermng의 거래유형 - FilterToggleGroup은 단일선택 전제라 거기선 못 쓴다).
export const Dot = ({ color }) => {
  if (!color) return null;
  return (
    <Box
      component="span"
      sx={{
        width: 7,
        height: 7,
        borderRadius: '50%',
        bgcolor: `${color}.main`,
        mr: 0.75,
        display: 'inline-block',
        flexShrink: 0,
      }}
    />
  );
};

// 필터 묶음 한 줄(라벨 + 토글버튼그룹)을 가로로 늘어놓는 공통 바깥 틀.
// className='status-chip-list'로 고정 - overlap 체커/전역 테마 CSS가 이 클래스를 참조한다.
export const FilterToggleRow = ({ children, className = 'status-chip-list', sx = {} }) => (
  <Box
    className={className}
    sx={{
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      // 승인된 기준(chancemng, 2026-10-22)은 검색카드 아래 좌측 정렬 - 화면별 sx로
      // flex-end로 뒤집지 않는다(2026-10-23 폴리시). 꼭 다른 정렬이 필요하면 여기
      // 기본값을 바꾸고 전체 화면을 재검증할 것.
      justifyContent: 'flex-start',
      rowGap: 1,
      columnGap: 3,
      width: '100%',
      ...sx,
    }}
  >
    {children}
  </Box>
);

// 묶음 하나: 라벨 + 단일선택 토글버튼그룹(size small, 한 줄 높이로 통일).
// options: [{ id, value, label, color }], newValue===null(같은 버튼 재클릭)이면 선택 유지.
const FilterToggleGroup = ({ label, options = [], value, onChange }) => {
  const handleChange = useCallback(
    (_event, newValue) => {
      if (newValue === null) return;
      onChange(newValue);
    },
    [onChange],
  );

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, rowGap: 0.5, flexWrap: 'wrap', minWidth: 0 }}>
      {label && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontWeight: 600, flexShrink: 0, whiteSpace: 'nowrap' }}
        >
          {label}
        </Typography>
      )}
      <ToggleButtonGroup
        size="small"
        exclusive
        value={value}
        onChange={handleChange}
        sx={{
          // 묶음이 좁은 화면(휴대폰 등)에서 버튼 수가 많으면 한 줄에 안 들어갈 수 있다 -
          // nowrap이면 넘친 버튼이 컨테이너 밖으로 그냥 잘려 보였다(2026-10-22, techmng
          // "단계" 6개 버튼 @414px 실측). wrap이면 그룹 안에서 다음 줄로 내려간다.
          flexWrap: 'wrap',
          rowGap: 0.5,
          '& .MuiToggleButton-root': {
            textTransform: 'none',
            px: 1.1,
            py: 0.4,
            lineHeight: 1.4,
            whiteSpace: 'nowrap',
          },
        }}
      >
        {options.map((opt) => (
          <ToggleButton key={opt.id ?? opt.value} value={opt.value} aria-label={opt.label}>
            <Dot color={opt.color} />
            {opt.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Box>
  );
};

export default FilterToggleGroup;
